import { useEffect, useState, useContext, } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../api2";
import {
    Box, Paper, Typography, Container, Select, MenuItem,
    FormControl, InputLabel,
} from "@mui/material";
import UserContext from "../User/UserSettings/UserContext";
import AutoCompleteHelper from "../Helpers/InputHelpers/AutoCompleteHelper";
import LabDataTableSetup from "./LabDataTableSetup";
import { LabDataItem } from "../Types/LabData";
import useNotification from "../Hooks/useNotification";
import LabReportsImages from "./LabReportsImages";
import setImage from "../Helpers/ImageHelpers/setImage";
import GraphBase from "./LabGraph/GraphBase";
import LabDataSubTable from "./LabDataSubTable";
import LabReportsImagesLoading from "./LabReportsImagesLoading";

interface location {
    locationName: string;
    id: number;
}

interface bin {
    binName: string;
}

interface ProcessedImage {
    imageData: string;
    metadata: any;
    [key: string]: any;
}

function LabReportsHome() {
    const [searchParams] = useSearchParams();
    const [locations, setLocations] = useState<location[]>([]);
    const notify = useNotification();
    const { user } = useContext(UserContext) || { user: null };
    const [bins, setBins] = useState<{ binName: string }[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<location | null>(null);
    const [selectedBin, setSelectedBin] = useState<bin | null>(null);
    const [labData, setLabData] = useState<Record<string, LabDataItem[]>>({});
    const [binLabData, setBinLabData] = useState<LabDataItem[]>([]);
    const [filteredLabData, setFilteredLabData] = useState<LabDataItem[]>([]);
    const [images, setImages] = useState<ProcessedImage[]>([]);
    const [cropYears, setCropYears] = useState<(string | number)[]>([]);
    const [selectedCropYear, setSelectedCropYear] = useState<string | number>("");
    const [imagesLoading, setImagesLoading] = useState(true);
    const [isAutoSelecting, setIsAutoSelecting] = useState(false);

    useEffect(() => {
        const getLocations = async function () {
            return await API.getLocationsByUser({ id: user.id })
        }
        getLocations().then((response) => {
            setLocations(response)
            const locationId = searchParams.get('locationId');
            const binName = searchParams.get('binName');
            const cropYear = searchParams.get('cropYear');
            if (locationId && binName && cropYear && response.length > 0 && !isAutoSelecting) {
                setIsAutoSelecting(true);

                // Find and auto-select the location
                const targetLocation = response.find((loc: any) => loc.id.toString() === locationId);
                if (targetLocation) {
                    setSelectedLocation(targetLocation);

                    // Store the target bin and crop year for later selection
                    sessionStorage.setItem('autoSelectBin', binName);
                    sessionStorage.setItem('autoSelectCropYear', cropYear);
                } else {
                    setIsAutoSelecting(false);
                }
            }
        })
    }, []);

    useEffect(() => {
        if (selectedLocation) {
            const getLocationLabData = async function () {
                return await API.getLocationLabData({ id: selectedLocation.id })
            }
            getLocationLabData().then((response) => {
                if (response.error) return notify(response.message, "error")
                // Transform the keys into objects with binName property

                const keys = Object.keys(response.data);
                const binObjects = keys.map(key => ({ binName: key }));
                setBins(binObjects);
                setLabData(response.data);

                // Auto-select bin if we're in auto-selection mode
                const autoSelectBinName = sessionStorage.getItem('autoSelectBin');
                if (isAutoSelecting && autoSelectBinName) {
                    const targetBin = binObjects.find(bin => bin.binName === autoSelectBinName);
                    if (targetBin) {
                        setSelectedBin(targetBin);
                    } else {
                        setIsAutoSelecting(false);
                    }
                    sessionStorage.removeItem('autoSelectBin'); // Clean up
                }
            })
        }
    }, [selectedLocation, isAutoSelecting]);

    useEffect(() => {
        if (selectedBin && labData) {
            // Get the data for the selected bin
            const binData = labData[selectedBin.binName];
            setBinLabData(binData || []);

            setFilteredLabData([]);
            setImages([]);

            // Extract unique crop years
            if (binData && binData.length > 0) {
                const uniqueYears = Array.from(new Set(binData.map(item => item.cropYear).filter(Boolean)));
                setCropYears(uniqueYears);

                // Only reset crop year if we're not in auto-selection mode
                // and if the current selected crop year is not in the new bin's data
                if (!isAutoSelecting) {
                    const currentYearExists = selectedCropYear && uniqueYears.find(year => year.toString() === selectedCropYear.toString());
                    if (!currentYearExists) {
                        setSelectedCropYear("");
                    }
                }
            } else {
                setCropYears([]);
                if (!isAutoSelecting) {
                    setSelectedCropYear("");
                }
                if (isAutoSelecting) {
                    setIsAutoSelecting(false);
                }
            }
        }
    }, [selectedBin, labData]);

    useEffect(() => {
        // Auto-select crop year if we're in auto-selection mode and crop years are available
        const autoSelectCropYear = sessionStorage.getItem('autoSelectCropYear');
        if (isAutoSelecting && autoSelectCropYear && cropYears.length > 0) {
            // Check if the target crop year exists in the available years
            const targetCropYear = cropYears.find(year => year.toString() === autoSelectCropYear);
            if (targetCropYear) {
                setSelectedCropYear(targetCropYear);
                sessionStorage.removeItem('autoSelectCropYear'); // Clean up
                setIsAutoSelecting(false); // Complete auto-selection
            } else {
                sessionStorage.removeItem('autoSelectCropYear'); // Clean up
                setIsAutoSelecting(false);
            }
        }
    }, [cropYears, isAutoSelecting])

    // Filter data and load images only when crop year is selected
    useEffect(() => {
        if (selectedCropYear && binLabData.length > 0) {
            // Filter the data to only include items with matching crop year
            const filtered = binLabData.filter(item => item.cropYear === selectedCropYear);
            console.log("Filtered Lab Data:", filtered);
            setFilteredLabData(filtered);

            // Only load images for the selected crop year
            getImages(filtered);
        } else {
            setFilteredLabData([]);
            setImages([]);
        }
    }, [selectedCropYear, binLabData]);

    const getImages = function (binData: LabDataItem[]) {
        console.log(binData)
        // Initialize an empty array to collect image promises and their metadata
        const imageRequests: { promise: Promise<any>; originalMetadata: any }[] = [];

        // Loop through each lab data item
        binData.forEach(item => {
            // Check if this item has images
            if (item.images && Array.isArray(item.images)) {
                // For each image object, make API call and store original metadata
                item.images.forEach(imageData => {
                    imageRequests.push({
                        promise: API.getImages(imageData),
                        originalMetadata: imageData // Store the original metadata
                    });
                });
            }
        });

        // Extract just the promises for Promise.all
        const imagePromises = imageRequests.map(req => req.promise);

        setImagesLoading(true);

        // Wait for all image requests to complete
        Promise.all(imagePromises)
            .then(results => {
                const processedImages: ProcessedImage[] = [];

                // Process each result with its corresponding metadata
                results.forEach((image, index) => {
                    // Get the original metadata that was sent with this request
                    const originalMetadata = imageRequests[index].originalMetadata;

                    // Process the image string
                    const processedImage = setImage({
                        imageString: typeof image === 'string' ? image : image.imageString || image
                    });

                    // Combine metadata from API response and original request
                    const metadata = {
                        ...originalMetadata,
                        ...(typeof image === 'object' && image.metadata ? image.metadata : {})
                    };

                    processedImages.push({
                        imageData: processedImage,
                        metadata: metadata,
                    });
                });

                setImages(processedImages);
                setImagesLoading(false);
            })
            .catch(error => {
                console.error("Failed to fetch images:", error);
                notify("Failed to load images", "error");
                setImages([]);
                setImagesLoading(false);
            });
    }


    return (<>
        <Container maxWidth="lg">
            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 3, mb: 2 }}>
                Lab Reports
            </Typography>

            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Select a storage location, bin, and crop year to view lab reports
                </Typography>
                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
                    <Box sx={{ width: "100%" }}>
                        <AutoCompleteHelper
                            name="locationName"
                            label="Select Storage"
                            optionsArray={locations}
                            value={selectedLocation}
                            setValue={setSelectedLocation}
                        />
                    </Box>
                    <Box sx={{ width: "100%" }}>
                        <AutoCompleteHelper
                            name="binName"
                            label="Select Bin"
                            optionsArray={bins}
                            value={selectedBin}
                            setValue={setSelectedBin}
                        />
                    </Box>
                    <FormControl sx={{ width: "100%" }}>
                        <InputLabel>Select Crop Year</InputLabel>
                        <Select
                            label="Select Crop Year"
                            value={selectedCropYear}
                            onChange={(e) => setSelectedCropYear(e.target.value)}
                            disabled={cropYears.length === 0}
                        >
                            {cropYears.map((year) => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Paper>
        </Container >
        {selectedBin && selectedCropYear && (<>
            <Typography variant="h6" gutterBottom>
                Lab Data for {selectedBin.binName} - {selectedCropYear} Crop Year
            </Typography>
            <LabDataTableSetup labData={filteredLabData} />
            <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 4 }}>
                <Box sx={{ width: { xs: '100%', lg: '50%' } }}>
                    <LabDataSubTable labData={filteredLabData} />
                </Box>
                <Box sx={{ width: { xs: '100%', lg: '50%' } }}>
                    {imagesLoading ? <LabReportsImagesLoading /> :
                        <LabReportsImages images={images} />}
                </Box>
            </Box>
            <GraphBase labData={filteredLabData} />
        </>)
        }
    </>);
}

export default LabReportsHome;